import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

export type BlogLang = 'ar' | 'en';
export type BlogCategory = 'all' | 'career' | 'cv' | 'travel' | 'interview';
export type BlogStatus = 'draft' | 'published';

@Injectable({
  providedIn: 'root',
})
export class BlogsService {
  constructor(private http: HttpClient) {}

  // =========================
  // Blogs
  // =========================

  getBlogs(filters: any = {}): Observable<any> {
    let params = new HttpParams();

    Object.keys(filters).forEach((key) => {
      if (
        filters[key] !== null &&
        filters[key] !== undefined &&
        filters[key] !== ''
      ) {
        // category=all مش محتاج يتبعت للباك
        if (key === 'category' && filters[key] === 'all') {
          return;
        }

        params = params.set(key, filters[key]);
      }
    });

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  getBlogBySlug(slug: any, lang?: BlogLang): Observable<any> {
    let params = new HttpParams();

    if (lang) {
      params = params.set('lang', lang);
    }

    return this.http.get(`${environment.Server_URL}/blogs/${slug}`, {
      params,
    });
  }

  addBlog(data: any): Observable<any> {
    return this.http.post(`${environment.Server_URL}/blogs`, data);
  }

  updateBlog(id: any, data: any): Observable<any> {
    return this.http.put(`${environment.Server_URL}/blogs/${id}`, data);
  }

  /**
   * Recommended when updating with image/FormData.
   * Backend handoff says:
   * POST /api/blogs/{id}
   * Do not rely on PUT with FormData if image exists.
   */
  updateBlogWithFormData(id: any, formData: FormData): Observable<any> {
    return this.http.post(`${environment.Server_URL}/blogs/${id}`, formData);
  }

  deleteBlog(id: any): Observable<any> {
    return this.http.delete(`${environment.Server_URL}/blogs/${id}`);
  }

  // =========================
  // Helpers
  // =========================

  getBlogsList(
    lang: BlogLang = 'en',
    category: BlogCategory = 'all',
    search: string = ''
  ): Observable<any> {
    let params = new HttpParams().set('lang', lang);

    if (category && category !== 'all') {
      params = params.set('category', category);
    }

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  getBlogsByCategory(
    category: Exclude<BlogCategory, 'all'>,
    lang: BlogLang = 'en'
  ): Observable<any> {
    const params = new HttpParams()
      .set('lang', lang)
      .set('category', category);

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  searchBlogs(
    search: string,
    lang: BlogLang = 'en',
    category: BlogCategory = 'all'
  ): Observable<any> {
    let params = new HttpParams()
      .set('lang', lang)
      .set('search', search);

    if (category && category !== 'all') {
      params = params.set('category', category);
    }

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  getPublishedBlogs(lang: BlogLang = 'en'): Observable<any> {
    const params = new HttpParams()
      .set('lang', lang)
      .set('status', 'published');

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  getDraftBlogs(lang: BlogLang = 'en'): Observable<any> {
    const params = new HttpParams()
      .set('lang', lang)
      .set('status', 'draft');

    return this.http.get(`${environment.Server_URL}/blogs`, { params });
  }

  getBlogDetails(slug: any, lang: BlogLang = 'en'): Observable<any> {
    const params = new HttpParams().set('lang', lang);

    return this.http.get(`${environment.Server_URL}/blogs/${slug}`, {
      params,
    });
  }

  // =========================
  // FormData Builder
  // =========================

  buildBlogFormData(data: any): FormData {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (value !== null && value !== undefined && value !== '') {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'boolean') {
          formData.append(key, value ? '1' : '0');
        } else if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    return formData;
  }
}